/**
 * User Details Component
 *
 * Displays comprehensive user information including profile details,
 * organization affiliation, and last login information. Also provides
 * functionality to update user details through an embedded form.
 *
 * Features:
 * - User profile information display
 * - Organization details and type
 * - Last login timestamp formatting
 * - Editable user details form
 * - Success/error toast notifications
 */

import React, { useState } from "react";

import {
  Alert,
  Card,
  Collapse,
  Divider,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";

import { User } from "../../../types";

import UserDetailsForm from "../../../components/users/user-details-form";
import { UserAvatar } from "../../../components/avatars";

interface UserDetailsCardProps {
  user: User;
}

const UserDetailsCard: React.FC<UserDetailsCardProps> = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  /**
   * Success handler for user details update
   */
  const onSuccess = () => {
    setSnackbar({
      open: true,
      message: "Details updated successfully",
      severity: "success",
    });
    setIsEditing(false);
  };

  /**
   * Error handler for user details update
   * @param error - The error object from the failed update
   */
  const onError = (error: unknown) => {
    console.log(error);
    setSnackbar({
      open: true,
      message: "Failed to update details",
      severity: "error",
    });
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Card>
      <Stack
        direction={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        spacing={2}
        padding={2}
        sx={{ width: "100%", boxSizing: "border-box" }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setIsEditing((prev) => !prev)}
          >
            {isEditing ? "Cancel" : "Edit"}
          </Typography>
        </Stack>

        <Stack spacing={2} alignItems="center">
          <UserAvatar
            text={user.fullName}
            sx={{ width: 100, height: 100, fontSize: 40 }}
          />
          <Stack alignItems="center">
            <Typography variant="h4">{user.fullName}</Typography>

            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle={"italic"}
              sx={{ mt: 1 }}
            >
              {user.jobTitle || "No job title provided"}
            </Typography>
          </Stack>
        </Stack>

        <Collapse
          in={isEditing}
          timeout={300}
          unmountOnExit
          sx={{ width: "100%" }}
        >
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Divider sx={{ width: "100%" }} />
            <UserDetailsForm onError={onError} onSuccess={onSuccess} />
          </Stack>
        </Collapse>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default UserDetailsCard;
